import { useEffect, useMemo, useState } from "react";
import type { GuestGrantView, InstanceMember, MemberEmailPreview, UserSummary } from "@keydials/api-client";
import type { InstanceSeatsView } from "@keydials/api-client";
import { ApiError } from "@keydials/api-client";
import { Icon } from "@keydials/dial-workspace";
import { HELP_ANCHORS, INSTANCE_ROLE_NAMES, instanceRoleDescription } from "@keydials/ui";
import { api, getAccessToken } from "../auth/AuthContext";
import { SlideOver } from "../components/chrome/Modal";
import { MemberFineTune } from "../components/chrome/modals";
import {
  assignableExcludingMembers,
  memberRows,
  sortMemberRows,
  type MemberRow,
  type MemberSortKey,
  type SortDirection,
} from "./memberRows";
import { DEFAULT_SEGMENT_NOUN, type SegmentNoun } from "@keydials/dial-workspace";
import { grantFailureMessage, inviteBlockedBySeats, seatLine } from "./grantFeedback";
import { ShareRefusalNote } from "./ShareRefusalNote";

export type MemberSection = { key: string; name: string; segments: { key: string; name: string }[] };

const th: React.CSSProperties = {
  textAlign: "left",
  fontSize: 12,
  fontWeight: 700,
  color: "var(--text-faint)",
  padding: "0 10px 8px",
  whiteSpace: "nowrap",
};

const td: React.CSSProperties = {
  padding: "10px",
  fontSize: 13.5,
  color: "var(--text)",
  borderTop: "var(--border-w) solid var(--border)",
  verticalAlign: "middle",
};

/** A sortable column header. Clicking the active column reverses it, matching every table people know. */
function SortHeader({
  label,
  col,
  sort,
  onSort,
}: {
  label: string;
  col: MemberSortKey;
  sort: { key: MemberSortKey; dir: SortDirection };
  onSort: (key: MemberSortKey) => void;
}) {
  const active = sort.key === col;
  return (
    <th style={th} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className="kd-row-hover"
        style={{
          font: "inherit",
          fontSize: 12,
          fontWeight: 700,
          color: active ? "var(--text)" : "var(--text-faint)",
          background: "none",
          border: "none",
          padding: "2px 4px",
          borderRadius: 6,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {label}
        {active && <Icon name={sort.dir === "asc" ? "chevDown" : "chevRight"} size={12} />}
      </button>
    </th>
  );
}

/**
 * Dial members (KEY-454): the people on this dial as a sortable table, with add and edit in a side sheet.
 *
 * Replaces the modal that listed one line per *grant*, so somebody with three scoped grants appeared three
 * times with nothing saying they were one person.
 */
export function MembersSection({
  instanceId,
  members,
  sections,
  canManage,
  canGrantAdmin,
  canAddOrgMembers,
  memberRefusal = null,
  onChanged,
  noun = DEFAULT_SEGMENT_NOUN,
}: {
  instanceId: string;
  members: InstanceMember[];
  sections: MemberSection[];
  canManage: boolean;
  canGrantAdmin: boolean;
  /** Holder of org.members.manage — adding somebody to the organisation stays an org-administrator act. */
  canAddOrgMembers: boolean;
  /**
   * Why this dial's organisation won't let anyone be given access here (KEY-556), or null. Shown only with
   * `canManage`: the policy says nothing about the caller's role, and a Reader was never going to add anyone.
   */
  memberRefusal?: string | null;
  onChanged: () => void | Promise<void>;
  /** This dial's word for an action point (KEY-539); omitted means the platform's word. */
  noun?: SegmentNoun;
}) {
  const [assignable, setAssignable] = useState<UserSummary[]>([]);
  const [guestUserIds, setGuestUserIds] = useState<ReadonlySet<string>>(new Set());
  const [sort, setSort] = useState<{ key: MemberSortKey; dir: SortDirection }>({ key: "name", dir: "asc" });
  const [sheet, setSheet] = useState<{ mode: "add" } | { mode: "edit"; row: MemberRow } | null>(null);
  const [seats, setSeats] = useState<InstanceSeatsView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = getAccessToken();
    if (!token || !canManage) return;
    api.listAssignableInstanceMembers(token, instanceId).then(setAssignable).catch(() => setAssignable([]));
    // Guests arrive as ordinary membership rows with no flag of their own, so the only way to tell them
    // apart is to ask which invites exist. A failure here costs a badge, not the page.
    api
      .listGuests(token, instanceId)
      .then((gs: GuestGrantView[]) => setGuestUserIds(new Set(gs.map((g) => g.userId))))
      .catch(() => setGuestUserIds(new Set()));
  }, [instanceId, canManage]);

  // The seat position (KEY-487). Re-read whenever the member list changes, because a grant that succeeded
  // just took a seat and a removal just gave one back. 404 for a caller who may not manage members, and
  // a host without billing answers `enforced: false`: both leave the sheet saying nothing about seats.
  useEffect(() => {
    const token = getAccessToken();
    if (!token || !canManage) return;
    api.getInstanceSeats(token, instanceId).then(setSeats).catch(() => setSeats(null));
  }, [instanceId, canManage, members]);

  const rows = useMemo(
    () => sortMemberRows(memberRows(members, { guestUserIds, canGrantAdmin, noun }), sort.key, sort.dir),
    [members, guestUserIds, canGrantAdmin, sort, noun],
  );

  const onSort = (key: MemberSortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  async function removeGrant(grantId: string) {
    const token = getAccessToken();
    if (!token) return;
    setError(null);
    try {
      await api.removeInstanceMemberGrant(token, instanceId, grantId);
      await onChanged();
    } catch {
      setError("Couldn't remove that access.");
    }
  }

  return (
    <div className="kd-card" style={{ padding: "var(--pad-card)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>Who's on this dial</div>
          <div style={{ fontSize: 12.5, color: "var(--text-faint)", marginTop: 2 }}>
            {rows.length} {rows.length === 1 ? "person" : "people"}
          </div>
        </div>
        {canManage && !memberRefusal && (
          <button
            type="button"
            className="kd-btn-primary kd-btn-sm"
            onClick={() => setSheet({ mode: "add" })}
            data-help-anchor={HELP_ANCHORS.memberGiveAccess}
          >
            Give access to…
          </button>
        )}
      </div>

      {canManage && memberRefusal && (
        <div style={{ marginBottom: 12 }}>
          <ShareRefusalNote text={memberRefusal} />
        </div>
      )}

      {error && (
        <div style={{ marginBottom: 12, fontSize: 13, color: "var(--st-overdue)" }} role="alert">
          {error}
        </div>
      )}

      {rows.length === 0 ? (
        <div style={{ padding: "18px 0", fontSize: 13.5, color: "var(--text-faint)" }}>
          Nobody has been given access to this dial yet.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
            <thead>
              <tr>
                <SortHeader label="Name" col="name" sort={sort} onSort={onSort} />
                <SortHeader label="Email" col="email" sort={sort} onSort={onSort} />
                <SortHeader label="Role" col="role" sort={sort} onSort={onSort} />
                <SortHeader label="Can reach" col="scope" sort={sort} onSort={onSort} />
                <th style={th} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.userId}>
                  <td style={td}>
                    <span style={{ fontWeight: 600 }}>{row.name}</span>
                    {row.isGuest && (
                      <span
                        title="Invited from outside the organisation, with an expiry"
                        style={{
                          marginLeft: 8, fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 99,
                          background: "var(--surface-inset)", color: "var(--text-faint)",
                        }}
                      >
                        Guest
                      </span>
                    )}
                  </td>
                  <td style={{ ...td, color: "var(--text-muted)" }}>{row.email}</td>
                  <td style={td}>{row.role}</td>
                  <td style={{ ...td, color: row.wholeDial ? "var(--text)" : "var(--text-muted)" }}>{row.scope}</td>
                  <td style={{ ...td, textAlign: "right" }}>
                    {canManage && !row.locked ? (
                      <button type="button" className="kd-btn kd-btn-sm" onClick={() => setSheet({ mode: "edit", row })}>
                        Edit
                      </button>
                    ) : (
                      <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
                        {row.locked ? "Managed elsewhere" : ""}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sheet?.mode === "add" && (
        <MemberSheet
          noun={noun}
          instanceId={instanceId}
          seats={seats}
          sections={sections}
          canGrantAdmin={canGrantAdmin}
          canAddOrgMembers={canAddOrgMembers}
          assignable={assignableExcludingMembers(assignable, members)}
          refusal={memberRefusal}
          onClose={() => setSheet(null)}
          onChanged={onChanged}
        />
      )}
      {sheet?.mode === "edit" && (
        <MemberSheet
          noun={noun}
          instanceId={instanceId}
          seats={seats}
          sections={sections}
          canGrantAdmin={canGrantAdmin}
          canAddOrgMembers={canAddOrgMembers}
          assignable={[]}
          editing={sheet.row}
          onRemoveGrant={removeGrant}
          refusal={memberRefusal}
          onClose={() => setSheet(null)}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}

/**
 * Add or edit one person's access.
 *
 * The default is the whole dial with a role — that is what almost every grant is, and the old form put
 * Role, Section and Segment side by side at equal weight, which made scoping look like a required step.
 * Scoping is kept, behind a disclosure, because per-section grants are a real shipped capability (PR #163)
 * and removing them would take access away from dials that rely on them.
 */
function MemberSheet({
  instanceId,
  sections,
  canGrantAdmin,
  canAddOrgMembers,
  assignable,
  editing,
  onRemoveGrant,
  refusal = null,
  seats,
  onClose,
  onChanged,
  noun = DEFAULT_SEGMENT_NOUN,
}: {
  instanceId: string;
  sections: MemberSection[];
  canGrantAdmin: boolean;
  canAddOrgMembers: boolean;
  assignable: UserSummary[];
  editing?: MemberRow;
  onRemoveGrant?: (grantId: string) => void | Promise<void>;
  /**
   * KEY-556: the organisation has shut the member channel. The sheet keeps Remove and the per-field
   * exceptions — neither hands the dial to anybody — and offers nothing the server would refuse.
   */
  refusal?: string | null;
  /** Where the paying organisation stands on seats; null when unknown or nobody is counting. */
  seats: InstanceSeatsView | null;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
  /** This dial's word for an action point (KEY-539); omitted means the platform's word. */
  noun?: SegmentNoun;
}) {
  const [userId, setUserId] = useState(editing?.userId ?? "");
  const [inviteEmail, setInviteEmail] = useState("");
  const [role, setRole] = useState("Reader");
  const [advanced, setAdvanced] = useState(false);
  const [sectionKey, setSectionKey] = useState("");
  const [segmentKey, setSegmentKey] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [preview, setPreview] = useState<MemberEmailPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const roleOptions = canGrantAdmin ? INSTANCE_ROLE_NAMES : INSTANCE_ROLE_NAMES.filter((r) => r !== "Admin");
  const section = sections.find((s) => s.key === sectionKey);
  const inviting = inviteEmail.trim().length > 0;
  const seat = seatLine(seats);
  const seatBlocked = inviteBlockedBySeats(seats, inviting);

  // Only fetches while the checkbox is on, so leaving it off costs nothing extra (KEY-XXX).
  useEffect(() => {
    const token = getAccessToken();
    const target = inviting ? inviteEmail.trim() : userId;
    if (!sendEmail || !token || !target || (inviting && !target.includes("@"))) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const t = setTimeout(() => {
      const req = inviting
        ? api.previewInviteInstanceMemberEmail(token, instanceId, { email: target, role })
        : api.previewInstanceMemberEmail(token, instanceId, { userId: target, role });
      req
        .then((p) => { if (!cancelled) { setPreview(p); setPreviewError(null); } })
        .catch((e) => { if (!cancelled) { setPreview(null); setPreviewError(e instanceof ApiError && e.message ? e.message : "Could not load the preview."); } })
        .finally(() => { if (!cancelled) setPreviewLoading(false); });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [sendEmail, inviting, userId, inviteEmail, role, instanceId]);

  async function grant() {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    const scope = advanced
      ? { sectionKey: sectionKey || null, segmentKey: sectionKey && segmentKey ? segmentKey : null }
      : { sectionKey: null, segmentKey: null };
    try {
      if (inviting) {
        const result = await api.inviteInstanceMember(token, instanceId, { email: inviteEmail.trim(), role, sendEmail, ...scope });
        const base = result.userCreated
          ? `Invited ${result.email} — they can sign in with that address.`
          : result.addedToOrganisation
            ? `${result.email} already had an account and has been added here.`
            : `${result.email} was already here — access updated.`;
        setNotice(sendEmail ? `${base} ${result.emailSent ? "They've been emailed about it." : "The email couldn't be sent, though."}` : base);
        setInviteEmail("");
      } else {
        if (!userId) return;
        const result = await api.addInstanceMember(token, instanceId, { userId, role, sendEmail, ...scope });
        if (sendEmail) {
          setNotice(result.emailSent ? "Access given, and they've been emailed about it." : "Access given, but the email couldn't be sent.");
        }
      }
      await onChanged();
      if (!inviting && !sendEmail) onClose();
    } catch (e) {
      // The server's own sentence where there is one: a seat refusal names the tier and the numbers,
      // which used to be thrown away in favour of "you may not have permission" (KEY-487).
      setError(grantFailureMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const field: React.CSSProperties = { width: "100%", marginTop: 6 };
  const labelStyle: React.CSSProperties = { fontSize: 12.5, fontWeight: 700, color: "var(--text-muted)" };

  return (
    <SlideOver title={editing ? `Access for ${editing.name}` : "Give access to…"} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {editing ? (
          <div>
            <div style={labelStyle}>Current access</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
              {editing.grants.map((g) => (
                <div key={g.grantId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, fontSize: 13 }}>
                  <span>
                    {g.role} · {!g.sectionKey && !g.segmentKey ? "Whole dial" : g.segmentKey ? `${g.sectionKey} / ${g.segmentKey}` : g.sectionKey}
                    {g.inherited && <span style={{ color: "var(--text-faint)" }}> · from the group</span>}
                  </span>
                  {!g.inherited && onRemoveGrant && (
                    <button type="button" className="kd-btn kd-btn-sm" onClick={() => onRemoveGrant(g.grantId)}>
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
            {/* KEY-299 per-field exceptions. Editing somebody's access is exactly where these belong —
                they were previously reached from a gear on the old member row. */}
            <div style={{ marginTop: 18 }}>
              <MemberFineTune instanceId={instanceId} userId={editing.userId} name={editing.name} />
            </div>
            {!refusal && <div style={{ ...labelStyle, marginTop: 18 }}>Add more access</div>}
          </div>
        ) : (
          <>
            <label style={labelStyle}>
              Person
              <select
                style={field}
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                disabled={inviting}
                data-help-anchor={HELP_ANCHORS.memberUserSelect}
              >
                <option value="">Choose someone…</option>
                {assignable.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName ?? u.email}
                  </option>
                ))}
              </select>
              {assignable.length === 0 && (
                <span style={{ display: "block", fontSize: 12, color: "var(--text-faint)", marginTop: 6 }}>
                  Everyone in this organisation already has access to this dial.
                </span>
              )}
            </label>

            {canAddOrgMembers && (
              <label style={labelStyle}>
                …or invite someone new
                <input
                  style={field}
                  type="email"
                  placeholder="name@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </label>
            )}
          </>
        )}

        {refusal ? (
          <ShareRefusalNote text={refusal} />
        ) : (
          <>
            {/* Scoping, kept but demoted. Closed by default because the whole dial is what almost every grant
                is; open it and the grant narrows to a section or one action point. */}
            <div>
              <button
                type="button"
                onClick={() => setAdvanced((v) => !v)}
                style={{
                  font: "inherit", fontSize: 12.5, fontWeight: 600, background: "none", border: "none",
                  color: "var(--text-muted)", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 5,
                }}
              >
                <Icon name={advanced ? "chevDown" : "chevRight"} size={13} />
                Advanced — limit to part of this dial
              </button>
              {advanced && (
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                  <label style={labelStyle}>
                    Section
                    <select style={field} value={sectionKey} onChange={(e) => { setSectionKey(e.target.value); setSegmentKey(""); }}>
                      <option value="">Whole dial</option>
                      {sections.map((s) => (
                        <option key={s.key} value={s.key}>{s.name}</option>
                      ))}
                    </select>
                  </label>
                  {section && (
                    <label style={labelStyle}>
                      {noun.One}
                      <select style={field} value={segmentKey} onChange={(e) => setSegmentKey(e.target.value)}>
                        <option value="">All of {section.name}</option>
                        {section.segments.map((g) => (
                          <option key={g.key} value={g.key}>{g.name}</option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>
              )}
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13 }}>
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
              Send them an email about this
            </label>
            {sendEmail && (
              previewLoading ? (
                <div style={{ fontSize: 12.5, color: "var(--text-faint)" }}>Loading preview…</div>
              ) : previewError ? (
                <div style={{ fontSize: 12.5, color: "var(--st-overdue)" }}>{previewError}</div>
              ) : preview ? (
                <div style={{ border: "var(--border-w) solid var(--border)", borderRadius: 8, padding: 10, fontSize: 12.5 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>{preview.subject}</div>
                  {/* Our own template engine's rendered HTML, not user input — same trust boundary as the admin Templates preview. */}
                  <div style={{ color: "var(--text-muted)" }} dangerouslySetInnerHTML={{ __html: preview.bodyHtml }} />
                </div>
              ) : (
                <div style={{ fontSize: 12.5, color: "var(--text-faint)" }}>
                  {inviting ? "Type a valid email to preview the message." : "Choose someone to preview the message."}
                </div>
              )
            )}

            {/* The seat position (KEY-487), shown while somebody decides whether to add another person. Every
                member costs a seat, read-only included (KEY-474). Only inviting somebody NEW is refused here
                when the plan is full: an existing organisation member may already hold a seat through another
                dial, so that path stays open and the server decides. */}
            {seat && (
              <div
                role={seatBlocked ? "alert" : undefined}
                style={{
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  color: seat.full ? "var(--st-overdue)" : "var(--text-muted)",
                  padding: "8px 10px",
                  borderRadius: 8,
                  background: "var(--surface-inset)",
                }}
              >
                {seat.text}
                {seat.full && !editing && (
                  <span>
                    {" "}
                    {seatBlocked
                      ? "Inviting someone new needs a free seat, so it can't go ahead."
                      : "Someone who already holds a seat on another dial can still be added."}
                  </span>
                )}
              </div>
            )}

            {/* Role sits directly above the button that acts on it (KEY-454) — it is the one choice every
                grant makes, and it used to be one of three equal-weight selects. */}
            <label style={labelStyle} data-help-anchor={HELP_ANCHORS.memberRoleSelect}>
              Role
              <select style={field} value={role} onChange={(e) => setRole(e.target.value)}>
                {roleOptions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <span style={{ display: "block", fontSize: 12, color: "var(--text-faint)", marginTop: 6, lineHeight: 1.5 }}>
                {instanceRoleDescription(role)}
              </span>
            </label>

            {error && <div role="alert" style={{ fontSize: 13, color: "var(--st-overdue)" }}>{error}</div>}
            {notice && <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{notice}</div>}

            <button
              type="button"
              className="kd-btn-primary"
              disabled={busy || (!editing && !userId && !inviting) || seatBlocked}
              onClick={grant}
              data-help-anchor={HELP_ANCHORS.memberAddGrant}
            >
              {busy ? "Saving…" : inviting ? "Invite & give access" : "Give access"}
            </button>
          </>
        )}
      </div>
    </SlideOver>
  );
}
